import { useLocation, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { getReviewRequestContextFromRoute } from 'src/contexts/ReviewDataProvider';
import { useToast, Button } from '@morressier/alembic';
import { useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { sendCommandWithOTT } from '../api/commands';
import { BrandLogo } from './SignUpInviteSent';
import axiosInstance from '../api/client';

type TokenData = {
  userId: string;
  action: string;
  entityId: string;
  submissionId: string;
  submissionContextId: string;
  organizationId: string;
};

export const AcceptReviews = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const searchParams = new URLSearchParams(location.search);

  const [isProcessing, setIsProcessing] = useState(false);
  const [lookupError, setLookupError] = useState(false);

  const { reviewId } = getReviewRequestContextFromRoute();
  const token = searchParams.get('token') || '';
  const userId = searchParams.get('userId') || '';

  const acceptReviewAndRedirect = async () => {
    if (!token) {
      setLookupError(true);
      return;
    }

    setIsProcessing(true);

    try {
      let tokenData: TokenData;
      try {
        const res = await axiosInstance.get<TokenData>(`/email/token/${token}`);
        tokenData = res.data;
      } catch (err) {
        setLookupError(true);
        if (isAxiosError(err) && err.status === 500) {
          toast({
            variant: 'attention',
            title:
              'Your review decision has already been processed and has moved to the next stage',
            isClosable: true
          });
          navigate('/app/home', { replace: true });
        } else {
          toast({
            variant: 'error',
            title: 'Unexpected Error',
            description:
              (isAxiosError(err) && err.response?.statusText) ??
              'Something went wrong...',
            isClosable: true
          });
        }
        return;
      }

      if (!tokenData.userId) {
        setLookupError(true);
        return;
      }

      const response = await sendCommandWithOTT({
        type: 'PeerReviewDecision',
        subtype: 'acceptRequest',
        userId: tokenData.userId,
        reviewId,
        submissionId: tokenData.submissionId,
        submissionContextId: tokenData.submissionContextId,
        organizationId: tokenData.organizationId,
        token
      });

      if (response) {
        toast({
          variant: 'success',
          title: 'Review was accepted successfully',
          description: 'Login to start reviewing',
          isClosable: true
        });
        navigate(`/app/reviews/${reviewId}`, {
          replace: true,
          state: {
            from: '/app/reviews?reviewListType=active'
          }
        });
      }
    } catch (err) {
      if (isAxiosError(err) && err.status === 401) {
        toast({
          variant: 'error',
          title: err.response?.statusText ?? 'Command Failed',
          description: 'You do not have permissions to accept the review',
          isClosable: true
        });
      } else if (isAxiosError(err) && err.status === 400) {
        toast({
          variant: 'attention',
          title: err.response?.data?.message ?? err.message,
          isClosable: true
        });
        navigate('/app/home', { replace: true });
      } else if (isAxiosError(err) && err.status === 500) {
        toast({
          variant: 'attention',
          title:
            'Your review decision has already been submitted and has moved to the next stage',
          isClosable: true
        });
        navigate('/app/home', { replace: true });
      } else {
        toast({
          variant: 'error',
          title: 'Unexpected Error',
          description:
            (isAxiosError(err) && err.response?.statusText) ??
            'Something went wrong...',
          isClosable: true
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (lookupError) return <Text>Error: invalid or expired token</Text>;
  if (!userId) return <Text>Error: missing userId parameter</Text>;

  return (
    <>
      <Flex direction="column" minH="100vh" w="full">
        <Flex
          py={2}
          px={4}
          justify="space-between"
          align="center"
          boxShadow="elevation3"
          position="sticky"
          top={0}
          zIndex={1000}
          bgColor="surfacePrimaryFill"
          flexShrink={0}
        >
          <Flex align="center" gap={2}>
            <BrandLogo />
          </Flex>
        </Flex>

        <Flex
          flex={1}
          direction="column"
          align="center"
          justify="center"
          textAlign="center"
          p={4}
        >
          <Text textStyle={'headingSection'} mb={1}>
            Review Invitation
          </Text>
          <Text textStyle={'textBody'} mb={6}>
            Please confirm that you want to accept this review request.
          </Text>

          <Button
            variant="primary"
            size="lg"
            onClick={acceptReviewAndRedirect}
            isLoading={isProcessing}
            loadingText="Accepting..."
          >
            Accept Review
          </Button>
        </Flex>
      </Flex>
    </>
  );
};
